/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import {loadModules} from 'esri-loader';
import {translate} from '@ion/core/src/locale/locale';
import {error} from '@ion/core/src/logger/logger';
import {FloatingIconButtonGroup} from '@ion/dls/src/components/buttons/FloatingIconButton';
import Tooltip from '@ion/dls/src/components/tooltip/Tooltip';
import {CloseFullScreenIcon} from '@ion/dls/src/icons/CloseFullScreenIcon';
import {OpenFullScreenIcon} from '@ion/dls/src/icons/OpenFullScreenIcon';
import {useMapFloatingIconButtonClass} from '@ion/dls/src/maps/toolbar/mapFloatingButtonStyles';
import ThemedComponent from '@ion/dls/src/theme/ThemedComponent';
import {IconSize, MarkerProps} from '@ion/dls/src/types/types';

type FullScreenToolProps = Omit<MarkerProps, 'color'> & {
  tooltipLabel?: string;
};

// defines the prop definition for FullScreenToolFactory component...
export interface FullScreenToolFactoryProps {
  view: __esri.MapView;
};

// defines the different state of full screen tool...
export enum State {
  active = 'active',
  ready = 'ready',
  unsupported = 'feature-unsupported',
  disabled= 'disabled'
};

/**
 * The FullScreenTool provides the UI for FullScreenTool icon that can be placed on the
 * map layers.
 *
 * ```typescript
 *
 *   <FullScreenTool size={IconSize.medium} onClick={function() {}} />
 *
 * ```
 */
export const FullScreenTool = ({size, disabled, isSelected, onClick, tooltipLabel}: FullScreenToolProps) => {
  const className = useMapFloatingIconButtonClass();
  return (
    <FloatingIconButtonGroup>
      <FloatingIconButtonGroup.FloatingIconButton onClick={onClick}
        className={className}
        disabled={disabled}
        tooltipLabel={tooltipLabel}>
        {!isSelected ? (
          <OpenFullScreenIcon size={size} />
        ) : (
          <CloseFullScreenIcon size={size} />
        )}
      </FloatingIconButtonGroup.FloatingIconButton>
    </FloatingIconButtonGroup>
  );
};

/**
 * The FullScreenToolFactory funtion is responsible for creating a FullScreen Tool/widget
 * component that provide an option to enter/exit full screen. This is a stand-alone
 * tool, can be usable in any of position on the map.
 *
 * ```typescript
 *
 *  import FullScreenToolFactory from '../tools/FullScreenTool';
 *
 *  const component = FullScreenToolFactory({view});
 *
 * ```
 */
export const FullScreenToolFactory = ({view}: FullScreenToolFactoryProps) => {
  const fullScreenRef               = useRef<__esri.Fullscreen | null>(null);
  const [disabled, setDisabled]     = useState(false);
  const [fullScreen, setFullScreen] = useState(false);

  const translations = useMemo(() => translate('maps:controls.fullscreen', {returnObjects: true}), []);

  const title = useMemo(() => {
    let title = translations.enter;
    if (disabled) {
      title = translations.noSupport;
    } else if (fullScreen) {
      title = translations.exit;
    }
    return title;
  }, [fullScreen, disabled, translations]);

  // loads the ESRI Fullscreen widget and derives `disabled` from its
  // support state. Guarded by `isMounted` since `loadModules` resolves
  // asynchronously - without it, unmounting before it resolves let the
  // widget get constructed and assigned (and setDisabled called) *after*
  // unmount, leaking the very widget this effect's own cleanup exists to
  // reclaim ...
  useEffect(() => {
    let isMounted = true;
    loadModules(['esri/widgets/Fullscreen'])
      .then(([Fullscreen]) => {
        if (isMounted) {
          fullScreenRef.current = new Fullscreen({view});
          const {state} = fullScreenRef.current?.viewModel || {};
          setDisabled(!state || state === State.unsupported || state === State.disabled);
        } else {
          new Fullscreen({view}).destroy();
        }
      })
      .catch((err) => {
        if (isMounted) {
          setDisabled(true);
        }
        error(err);
      });
    return () => {
      isMounted = false;
      fullScreenRef.current?.destroy();
    };
  }, []);

  // keeps `fullScreen` in sync when the browser's own fullscreen state
  // changes outside of this tool's own click handler - e.g. the user
  // pressing ESC to exit. Without this, `onClick`'s state update never
  // fires for an ESC-exit, leaving this tool's icon/title stuck on
  // "exit fullscreen" and the next click re-entering fullscreen instead
  // of exiting it ...
  useEffect(() => {
    function onFullscreenChange() {
      setFullScreen(!!document.fullscreenElement);
    }
    document.addEventListener('fullscreenchange', onFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', onFullscreenChange);
    };
  }, []);

  const onClick = useCallback(() => {
    fullScreenRef.current?.viewModel?.toggle();
    setFullScreen(!fullScreen);
  }, [fullScreen]);

  // current theme shares the same floating tooltip every other current-theme
  // map widget uses (FloatingIconButton's own tooltipLabel prop) instead of
  // this factory's own separate, differently-styled Tooltip wrap - legacy
  // theme keeps that original wrap untouched...
  return (
    <ThemedComponent currentComponent={
      <FullScreenTool isSelected={fullScreen}
        disabled={disabled}
        size={IconSize.medium}
        onClick={onClick}
        tooltipLabel={title} />
    } legacyComponent={
      <Tooltip title={title}>
        <div>
          <FullScreenTool isSelected={fullScreen}
            disabled={disabled}
            size={IconSize.medium}
            onClick={onClick} />
        </div>
      </Tooltip>
    } />
  );
};

export default memo(FullScreenToolFactory);
